/**
 * SetRateDialog — the shared Set/Change rate dialog (0132, B1-D3).
 *
 * Extracted verbatim from the Pre-billing RatesCard so the Admin >
 * Client rates grid opens the SAME dialog (one write surface, one set
 * of plain-words validation copy). Two callers:
 *   - RatesCard (client record > Pre-billing): onSaved seeds the
 *     per-client rate-history cache from the PUT's echo — behavior
 *     identical to before the extraction;
 *   - ClientRatesTab (Admin console): onSaved invalidates the
 *     tenant-wide overview query so the grid refetches.
 *
 * "Hourly rate" shows the dollar sign inside its box and the band's
 * example amount in a help line; "Starts on" says what the date means.
 * "Save rate" is disabled only while sending: pressed with an empty or
 * malformed amount, or no real date, it sends nothing, shows each gap's
 * sentence under its box and moves focus to the first. The amount's
 * problem line shows on leaving the box or on a press, never while
 * typing, and clears on the next change. A Change pressed with the same
 * amount and today's date closes without sending. The dialog validates
 * politely (rateInputProblem — the server re-validates), PUTs the
 * canonical 2-decimal string (money is never a float), and hands the
 * echoed refreshed history to onSaved before closing. The date defaults
 * to the caller-injected `today` — no Date.now() here.
 *
 * A per-visit band's box reads "Rate per visit". The Rates card opens the
 * dialog for a whole row: with `afterHours`, a second box changes the
 * band's after-hours rate from the same date. Each box sends only when
 * its amount or the date changed; an empty after-hours box that opened
 * empty sends nothing. When the first save lands and the second fails,
 * the card takes the first's history and the dialog stays open naming
 * the after-hours rate.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import { FLD_MONEY, FLD_MONEY_SIGN } from '@/components/primitives/modalFormStyles';
import { ApiError } from '@/lib/api';
import { contactSentence, saveFailureText, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import type { RateBandOption } from '@/lib/notes';
import {
  canonicalRateInput,
  putClientRate,
  rateExample,
  rateInputProblem,
  type ClientRateRowWire,
} from '@/lib/clientRates';
import { DateInput } from '@/components/primitives/DateInput';

/** The dialog's fact-shaped save errors, plain words. */
function saveErrorMessage(err: unknown, contact: SupportContact, band: string): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return contactSentence("Setting rates isn't part of your access. Ask {contact_billing} to set this one.", contact);
    }
    if (err.status === 422) return "That rate band isn't offered any more. Choose one from the list.";
    if (err.status === 400) return `Enter the amount as dollars and cents, with no $ sign: ${rateExample(band)}. Then pick a start date.`;
  }
  return (
    saveFailureText(err, contact) ??
    contactSentence(
      "That rate didn't save. Check the amount and the start date, and if it still won't save, tell {contact_system}.",
      contact,
    )
  );
}

export interface SetRateDialogProps {
  band: RateBandOption;
  clientId: string;
  /** Prefill for a correction; '' for a first rate. */
  initialRate: string;
  today: string;
  /** The date the box opens with (default `today`): a rate that has not
   *  started opens on its own start date, so a corrected amount replaces
   *  it rather than adding a second row. */
  initialFrom?: string;
  /** The row's after-hours band and its current amount ('' for none). */
  afterHours?: { band: RateBandOption; initialRate: string };
  /** Dialog title override (default: `{band.label} rate`). The Admin
   *  grid passes the client's name so the operator always sees whose
   *  rate is open. */
  title?: string;
  /** Receives the PUT's echoed refreshed history (the caller decides
   *  whether to seed a cache or refetch an aggregate). */
  onSaved: (rates: ClientRateRowWire[]) => void;
  onClose: () => void;
}

export function SetRateDialog({
  band,
  clientId,
  initialRate,
  today,
  initialFrom,
  afterHours,
  title,
  onSaved,
  onClose,
}: SetRateDialogProps): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const openedFrom = initialFrom ?? today;
  const [rate, setRate] = useState(initialRate);
  const [ahRate, setAhRate] = useState(afterHours?.initialRate ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(openedFrom);
  const [error, setError] = useState<string | null>(null);
  const contact = useSupportContact();
  /** The amount's problem shows once the box is left or Save is pressed. */
  const [problemShown, setProblemShown] = useState(false);
  const [dateGap, setDateGap] = useState(false);
  const amountRef = useRef<HTMLInputElement | null>(null);
  const ahRef = useRef<HTMLInputElement | null>(null);
  const dateRef = useRef<HTMLInputElement | null>(null);
  const [ahProblemShown, setAhProblemShown] = useState(false);

  const perVisit = band.per_visit === true;
  // With an after-hours box, a rate box that opened empty may stay empty
  // (only the after-hours rate is being set), unless both are empty.
  const rateLeftEmpty =
    afterHours !== undefined && initialRate === '' && rate.trim() === '' && ahRate.trim() !== '';
  const problem = rateLeftEmpty ? null : rateInputProblem(rate, band.value);
  // An after-hours box left empty means no change; anything typed must read.
  const ahProblem =
    afterHours === undefined || ahRate.trim() === '' ? null : rateInputProblem(ahRate, afterHours.band.value);
  const showAhProblem = ahProblemShown && ahProblem !== null;
  const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom);
  const showProblem = problemShown && problem !== null;

  // The request is built where Save is pressed and travels as the
  // mutation's variables: React Query hands a mutation its options in an
  // effect, so a mutationFn reading this render's values could send an
  // earlier render's.
  // A first save that fails throws. A second (the after-hours rate) that
  // fails after the first landed resolves with the first's history and
  // the second's failure, so the card still shows what saved.
  const saveMut = useMutation<
    { rates: ClientRateRowWire[]; laterFailure: unknown },
    unknown,
    { bodies: Array<{ rate_band: string; rate: string; effective_from: string }> }
  >({
    mutationFn: async ({ bodies }) => {
      let rates: ClientRateRowWire[] = [];
      for (const [i, body] of bodies.entries()) {
        try {
          rates = await putClientRate(tenantSlug, clientId, body);
        } catch (err) {
          if (i === 0) throw err;
          return { rates, laterFailure: err };
        }
      }
      return { rates, laterFailure: undefined };
    },
    onSuccess: ({ rates, laterFailure }, { bodies }) => {
      // The PUT echoes the refreshed history — the caller re-renders from it.
      onSaved(rates);
      if (laterFailure !== undefined) {
        setError(
          `The rate saved; the after-hours rate didn't. ${saveErrorMessage(laterFailure, contact, bodies[1]!.rate_band)}`,
        );
        return;
      }
      onClose();
    },
    onError: (err, { bodies }) => setError(saveErrorMessage(err, contact, bodies[0]!.rate_band)),
  });

  const sameDate = effectiveFrom === openedFrom;
  const rateUnchanged =
    rateLeftEmpty ||
    (initialRate !== '' && problem === null && canonicalRateInput(rate) === canonicalRateInput(initialRate) && sameDate);
  const ahUnchanged =
    afterHours === undefined ||
    ahRate.trim() === '' ||
    (ahProblem === null &&
      afterHours.initialRate !== '' &&
      canonicalRateInput(ahRate) === canonicalRateInput(afterHours.initialRate) &&
      sameDate);

  function pressSave(): void {
    if (rateUnchanged && ahUnchanged) {
      onClose();
      return;
    }
    if (problem !== null || ahProblem !== null || !dateOk) {
      setProblemShown(true);
      setAhProblemShown(true);
      setDateGap(!dateOk);
      (problem !== null ? amountRef : ahProblem !== null ? ahRef : dateRef).current?.focus();
      return;
    }
    const bodies: Array<{ rate_band: string; rate: string; effective_from: string }> = [];
    if (!rateUnchanged) bodies.push({ rate_band: band.value, rate: canonicalRateInput(rate), effective_from: effectiveFrom });
    if (!ahUnchanged && afterHours !== undefined) {
      bodies.push({ rate_band: afterHours.band.value, rate: canonicalRateInput(ahRate), effective_from: effectiveFrom });
    }
    saveMut.mutate({ bodies });
  }

  return (
    <Dialog
      title={title ?? `${band.label} rate`}
      size="sm"
      testId="set-rate-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={saveMut.isPending}
            loading={saveMut.isPending}
            data-testid="set-rate-save"
            onClick={pressSave}
          >
            Save rate
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2 py-2">
        <Field
          label={perVisit ? 'Rate per visit' : 'Hourly rate'}
          required
          htmlFor="set-rate-amount"
          help={`Dollars and cents, for example ${rateExample(band.value)}.`}
        >
          <div className="relative">
            <span aria-hidden="true" className={FLD_MONEY_SIGN}>
              $
            </span>
            <input
              ref={amountRef}
              id="set-rate-amount"
              data-testid="set-rate-amount"
              type="text"
              inputMode="decimal"
              className={FLD_MONEY}
              value={rate}
              aria-required="true"
              aria-invalid={showProblem ? 'true' : undefined}
              aria-describedby={showProblem ? 'set-rate-amount-help set-rate-amount-error' : 'set-rate-amount-help'}
              onBlur={() => setProblemShown(true)}
              onChange={(e) => {
                setError(null);
                setProblemShown(false);
                setRate(e.target.value);
              }}
            />
          </div>
        </Field>
        {showProblem && (
          <p id="set-rate-amount-error" role="alert" data-testid="set-rate-problem" className="text-xs text-pepper">
            {problem}
          </p>
        )}
        {afterHours !== undefined && (
          <>
            <Field
              label="After-hours hourly rate"
              htmlFor="set-rate-ah-amount"
            >
              <div className="relative">
                <span aria-hidden="true" className={FLD_MONEY_SIGN}>
                  $
                </span>
                <input
                  ref={ahRef}
                  id="set-rate-ah-amount"
                  data-testid="set-rate-ah-amount"
                  type="text"
                  inputMode="decimal"
                  className={FLD_MONEY}
                  value={ahRate}
                  aria-invalid={showAhProblem ? 'true' : undefined}
                  aria-describedby={showAhProblem ? 'set-rate-ah-amount-error' : undefined}
                  onBlur={() => setAhProblemShown(true)}
                  onChange={(e) => {
                    setError(null);
                    setAhProblemShown(false);
                    setAhRate(e.target.value);
                  }}
                />
              </div>
            </Field>
            {showAhProblem && (
              <p id="set-rate-ah-amount-error" role="alert" data-testid="set-rate-ah-problem" className="text-xs text-pepper">
                {ahProblem}
              </p>
            )}
          </>
        )}
        <Field
          label="Starts on"
          required
          htmlFor="set-rate-from"
          help="Time before this date keeps its old rate."
          error={dateGap ? 'Choose the date this rate starts.' : undefined}
        >
          <DateInput
            ref={dateRef}
            id="set-rate-from"
            data-testid="set-rate-from"
            size="field" className="w-full max-w-[11rem]"
            value={effectiveFrom}
            aria-required="true"
            aria-invalid={dateGap ? 'true' : undefined}
            aria-describedby={dateGap ? 'set-rate-from-help set-rate-from-error' : 'set-rate-from-help'}
            onChange={(e) => {
              setError(null);
              setDateGap(false);
              setEffectiveFrom(e.target.value);
            }}
          />
        </Field>
        {error !== null && (
          <p role="alert" data-testid="set-rate-error" className="text-xs text-pepper">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
